import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Input, Select, Table, Modal, Descriptions, Button, Space, message } from 'antd';
import { BuyerManagementPageModel } from '../pageModels/BuyerManagementPageModel';
import { globalStore } from '../../models/globalStore';
import '../styles/BuyerManagement.css';
import { ImagePreview } from '../../components/ImagePreview';

const { Option } = Select;

const BuyerManagement: React.FC = () => {
    const [model] = useState(() => new BuyerManagementPageModel());
    const [blacklistRecordsVisible, setBlacklistRecordsVisible] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewSrc, setPreviewSrc] = useState<string>('');

    useEffect(() => {
        globalStore.selectedMenuKey = 'buyer-management';
        model.loadBuyers();
    }, []);

    const handleSearch = () => {
        model.loadBuyers();
    };

    const handleReset = async () => {
        await model.resetFilters();
    };

    const openSignaturePreview = () => {
        if (!model.selectedBuyer?.signatureUrl) return;
        setPreviewSrc(model.selectedBuyer.signatureUrl);
        setPreviewVisible(true);
    };

    const closePreview = () => setPreviewVisible(false);

    const getIsCurrentlyBlacklisted = () => {
        if (!model.selectedBuyer?.blacklistRecords?.length) return false;
        const lastRecord = model.selectedBuyer.blacklistRecords[model.selectedBuyer.blacklistRecords.length - 1];
        return lastRecord?.type === 0;
    };

    const isCurrentlyBlacklisted = getIsCurrentlyBlacklisted();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: '昵称',
            dataIndex: 'nickname',
            key: 'nickname',
            render: (nickname: string, record: any) => {
                const records = record.blacklistRecords ?? [];
                const isBlacklisted = records.length > 0 &&
                    records[records.length - 1].type === 0;
                
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {nickname}
                        {isBlacklisted && (
                            <span style={{ 
                                background: '#ff4d4f', 
                                color: 'white', 
                                fontSize: '11px', 
                                padding: '1px 6px', 
                                borderRadius: '8px',
                                lineHeight: '1.2',
                                fontWeight: '500'
                            }}>
                                已拉黑
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            title: '类型',
            dataIndex: 'type',
            key: 'type',
            render: (type: number) => type === 0 ? '个人' : '企业',
        },
        {
            title: '手机号',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: '总订单数',
            dataIndex: 'totalOrders',
            key: 'totalOrders',
            sorter: true,
        },
        {
            title: '咨询转化率',
            dataIndex: 'consultConversionRate',
            key: 'consultConversionRate',
            render: (rate: number) => `${(rate * 100).toFixed(1)}%`,
            sorter: true,
        },
        {
            title: '等级',
            dataIndex: 'levelDesc',
            key: 'levelDesc',
        },
    ];

    const renderDetailModal = () => {
        if (!model.selectedBuyer) return null;

        const buyer = model.selectedBuyer;

        return (
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <div>{`买家-${buyer.nickname}${buyer.status === 0 ? '（有效）' : '（已注销）'}`}</div>
                        <Space style={{ marginLeft: 20 }}>
                            <Button
                                type={isCurrentlyBlacklisted ? 'primary' : 'default'}
                                danger
                                onClick={() => model.setShowBlacklistModal(true)}
                            >
                                {isCurrentlyBlacklisted ? '取消拉黑' : '拉黑'}
                            </Button>
                            <Button type="link" style={{ padding: 0 }} onClick={() => setBlacklistRecordsVisible(true)}>
                                查看拉黑记录
                            </Button>
                        </Space>
                    </div>
                }
                open={model.showDetailModal}
                onCancel={() => model.setSelectedBuyer(null)}
                width={860}
                footer={null}
                className="buyer-detail-modal"
            >
                <div>
                    <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px' }}>基本信息</div>
                    <Descriptions bordered column={4} size="small" labelStyle={{ padding: '4px 6px', fontSize: 12 }} contentStyle={{ padding: '4px 6px', fontSize: 12 }}>
                        <Descriptions.Item label="ID">{buyer.id}</Descriptions.Item>
                        <Descriptions.Item label="昵称">{buyer.nickname}</Descriptions.Item>
                        <Descriptions.Item label="真实姓名">{buyer.realName}</Descriptions.Item>
                        <Descriptions.Item label="类型">{buyer.type === 0 ? '个人' : '企业'}</Descriptions.Item>
                        {buyer.type === 1 && (
                            <Descriptions.Item label="企业名称">{buyer.enterpriseName}</Descriptions.Item>
                        )}
                        <Descriptions.Item label="手机号">{buyer.phone}</Descriptions.Item>
                        <Descriptions.Item label="账号状态">{buyer.status === 0 ? '有效' : '已注销'}</Descriptions.Item>
                        <Descriptions.Item label="是否实名">{buyer.isVerified ? '是' : '否'}</Descriptions.Item>
                        <Descriptions.Item label="微信号">{buyer.wechat}</Descriptions.Item>
                        <Descriptions.Item label="抖音号">{buyer.douyin}</Descriptions.Item>
                        <Descriptions.Item label="注册时间">{new Date(buyer.registerTime).toLocaleString()}</Descriptions.Item>
                        {buyer.signatureUrl && (
                            <Descriptions.Item label="签名">
                                <a onClick={openSignaturePreview} style={{ color: '#1677ff' }}>查看签名</a>
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px', marginTop: '10px' }}>经营数据</div>
                    <Descriptions bordered column={5} size="small" layout="vertical" labelStyle={{ padding: '0 0px', fontSize: 12 }} contentStyle={{ padding: '2px 6px', fontSize: 12, lineHeight: 1.1 }}>
                        <Descriptions.Item label="总咨询数">{buyer.totalConsults}</Descriptions.Item>
                        <Descriptions.Item label="总订单数">{buyer.totalOrders}</Descriptions.Item>
                        <Descriptions.Item label="总成交额">￥{buyer.totalAmount.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="咨询转化率">{(buyer.consultConversionRate * 100).toFixed(1)}%</Descriptions.Item>
                        <Descriptions.Item label="等级">{buyer.levelDesc}</Descriptions.Item>
                    </Descriptions>

                    {buyer.type === 1 && (
                        <>
                            <div style={{ fontSize: 14, fontWeight: 100, margin: '2px 0 6px', marginTop: '10px' }}>企业信息</div>
                            <Descriptions bordered column={2} size="small" labelStyle={{ padding: '4px 6px', fontSize: 12 }} contentStyle={{ padding: '4px 6px', fontSize: 12 }}>
                                <Descriptions.Item label="企业名称">{buyer.enterpriseName}</Descriptions.Item>
                                <Descriptions.Item label="企业账户余额">￥{buyer.enterpriseBalance?.toLocaleString()}</Descriptions.Item>
                            </Descriptions>
                        </>
                    )}
                </div>
            </Modal>
        );
    };

    const renderBlacklistModal = () => (
        <Modal
            title={isCurrentlyBlacklisted ? '取消拉黑' : '拉黑买家'}
            open={model.showBlacklistModal}
            onCancel={() => model.setShowBlacklistModal(false)}
            onOk={() => {
                const input = document.getElementById('blacklistReason') as HTMLInputElement;
                if (!input.value) {
                    message.error('请输入原因');
                    return;
                }
                if (model.selectedBuyer) {
                    const type = isCurrentlyBlacklisted ? 1 : 0;
                    model.updateBlacklistStatus(model.selectedBuyer.id, type, input.value);
                    model.setShowBlacklistModal(false);
                }
            }}
        >
            <Input.TextArea
                id="blacklistReason"
                placeholder="请输入原因"
                rows={4}
                value={model.blacklistReason}
                onChange={e => model.setBlacklistReason(e.target.value)}
            />
        </Modal>
    );

    const renderBlacklistRecordsModal = () => (
        <Modal
            title="拉黑记录"
            open={blacklistRecordsVisible}
            onCancel={() => setBlacklistRecordsVisible(false)}
            footer={null}
            width={720}
        >
            {model.selectedBuyer ? (
                !(model.selectedBuyer.blacklistRecords?.length) ? (
                    <div style={{ color: '#999' }}>暂无记录</div>
                ) : (
                    <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 6 }}>
                        {model.selectedBuyer.blacklistRecords.map((record, index) => (
                            <div key={index} style={{ padding: '4px 6px', background: index % 2 === 0 ? '#fafafa' : '#fff', border: '1px solid #f0f0f0', borderRadius: 0, marginBottom: 6, fontSize: 12 }}>
                                <Space size={12}>
                                    <span>操作：{record.type === 0 ? '拉黑' : '取消拉黑'}</span>
                                    <span>原因：{record.reason}</span>
                                    <span>时间：{new Date(record.timestamp).toLocaleString()}</span>
                                </Space>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                <div style={{ color: '#999' }}>暂无数据</div>
            )}
        </Modal>
    );

    return (
        <div className="buyer-management">
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-item"> 
                        <Input.Search
                            placeholder="搜索买家手机号/ID/昵称/真实姓名"
                            style={{ width: 300 }}
                            value={model.filter.searchText}
                            onChange={e => model.setFilter({ searchText: e.target.value })}
                        />
                    </div>
                    <div className="filter-item"> 
                        <Select
                            style={{ width: 120 }}
                            value={model.filter.type}
                            onChange={value => model.setFilter({ type: value })}
                        >
                            <Option value={-1}>全部类型</Option>
                            <Option value={0}>个人</Option>
                            <Option value={1}>企业</Option>
                        </Select>
                    </div>
                    <div className="filter-item"> 
                        <Select
                            style={{ width: 120 }}
                            value={model.filter.isVerified}
                            onChange={value => model.setFilter({ isVerified: value })}
                        >
                            <Option value={-1}>全部认证</Option>
                            <Option value={1}>已实名</Option>
                            <Option value={0}>未实名</Option>
                        </Select>
                    </div>
                    <div className="filter-item"> 
                        <Select
                            style={{ width: 200 }}
                            value={`${model.filter.sortField}_${model.filter.sortOrder}`}
                            onChange={value => {
                                const [field, order] = value.split('_');
                                model.setFilter({
                                    sortField: field as any,
                                    sortOrder: order as 'asc' | 'desc'
                                });
                            }}
                        >
                            <Option value="registerTime_desc">按注册时间倒序</Option>
                            <Option value="level_asc">按等级正序</Option>
                            <Option value="level_desc">按等级倒序</Option>
                            <Option value="totalConsults_asc">按咨询数正序</Option>
                            <Option value="totalConsults_desc">按咨询数倒序</Option>
                            <Option value="totalOrders_asc">按订单数正序</Option>
                            <Option value="totalOrders_desc">按订单数倒序</Option>
                            <Option value="consultConversionRate_asc">按转化率正序</Option>
                            <Option value="consultConversionRate_desc">按转化率倒序</Option>
                        </Select>
                    </div>

                    <div className="filter-buttons">
                        <Button type="primary" onClick={handleSearch}>查询</Button>
                        <Button onClick={handleReset}>重置</Button>
                    </div>
                    
                </div> 
            </div>

            <Table
                columns={columns}
                dataSource={model.buyers}
                loading={model.loading}
                rowKey="id"
                size="small"
                onRow={record => ({
                    onClick: () => model.setSelectedBuyer(record)
                })}
                pagination={false}
            />

            {renderDetailModal()}
            {renderBlacklistModal()}
            {renderBlacklistRecordsModal()}
            <ImagePreview visible={previewVisible} src={previewSrc} title="签名预览" onClose={closePreview} />
        </div>
    );
};

export default observer(BuyerManagement); 